import React from "react";
import ResearchLayout from "./shared/ResearchLayout";

const AWM = () => {
  const bgImage = process.env.PUBLIC_URL + "/awm-1.png";
  const featureImage = process.env.PUBLIC_URL + "/awm-2.png";

  return (
    <ResearchLayout
      title="AWM: Automated Workflow Management at Scale"
      subtitle="A framework for eliminating operational friction through intelligent orchestration and automated scalability."
      date="February 10, 2023"
      author="FOZZIL Research"
      category="Research"
      heroImage={bgImage}
      heroImageCaption="Fig 1. Schematic representation of the AWM orchestration layer."
      relatedLinks={[
         { title: "The AI Museum: A new format for public engagement", date: "Feb 17, 2025", category: "Proposal", path: "/research/ai-museum", image: process.env.PUBLIC_URL + '/ai-museum-hero.png' },
         { title: "T.A.E.L.S", date: "Jan 12, 2025", category: "Product", path: "/taels", image: process.env.PUBLIC_URL + '/fozzil-intelligence-core.png' }
      ]}
    >
      <div className="space-y-8 text-justify">
        <p>
          Automated Workflow Management (AWM) represents a shift from manual task execution to intelligent 
          process orchestration. By automating repetitive workflows at a global scale, AWM allows organizations 
          to eliminate operational friction and focus on high-value strategic initiatives.
        </p>
        
        <p>
          Traditional workflow management systems often suffer from latency, manual handoffs, and a lack of 
          real-time synchronization. AWM addresses these challenges by providing a unified platform that integrates 
          global operations, instant data synchronization, and seamless team collaboration.
        </p>

        <blockquote className="border-l-4 border-blue-500 pl-4 py-2 my-8 italic text-gray-300 bg-white/5 pr-4 rounded-r">
          "Stop managing tasks. Start orchestrating success."
        </blockquote>

        <p>
          AWM is built on three foundational pillars designed to maximize efficiency. First, the system is architected to manage workflows across continents without performance degradation, ensuring that teams in different time zones can collaborate on the same projects without version conflicts. Second, real-time data synchronization is critical for modern enterprises (Fig. 2). AWM leverages advanced state management to ensure that updates are propagated instantly across all connected nodes.
        </p>

        <figure className="my-8">
           <img src={featureImage} alt="AWM Dashboard Interface" className="rounded-sm border border-white/10 w-full" />
           <figcaption className="mt-2 text-sm text-gray-500 font-mono">Fig 2. Visual analytics dashboard tracking real-time performance metrics.</figcaption>
        </figure>

        <p>
          Third, by automating handoffs between teams, AWM reduces the cognitive load on employees and minimizes the risk of human error during critical process transitions.
        </p>

        <p>
          AWM enables instant execution, allowing complex workflows to be triggered with a single click or automated event triggers—eliminating latency in favor of instant action. Additionally, visual analytics track performance bottlenecks and process efficiency in real-time dashboards designed to clarify rather than confuse.
        </p>

        <p>
          AWM redefines efficiency by moving beyond simple task lists to comprehensive workflow orchestration. 
          With its focus on scalability and automation, it provides the infrastructure necessary for 
          modern organizations to thrive in a fast-paced environment.
        </p>

        <div className="mt-12 p-10 md:p-16 bg-black border border-white/5 rounded-[40px] relative overflow-hidden group">
           <div className="absolute top-0 left-0 w-full h-full bg-blue-600/5 blur-[100px] pointer-events-none" />
           <h4 className="text-2xl font-medium mb-4 text-white uppercase tracking-tighter relative z-10 transition-colors group-hover:text-blue-400">Scale Effortlessly</h4>
           <p className="text-zinc-500 font-light relative z-10 text-lg leading-relaxed group-hover:text-zinc-400 transition-colors">
              Automate. Optimize. Sync. The future of work is orchestrated by AWM.
           </p>
        </div>
      </div>
    </ResearchLayout>
  );
};

export default AWM;
